// @flow

import * as React from 'react';
import { type CloseIconColorVariant } from '@ta/modal.core';
import Modal from '@ta/modal.styleguide';

/** OuterProps values are passed through to Modal Props unless otherwise stated. */
export type OuterProps = {|
  /** Takes up the entire width and height of the modal
   * (i.e., no padding) */
  isFullBleed?: boolean,

  /** Optional header content */
  header?: React.Node,

  /** Forces use of FullScreenModal.  Takes precedent over disableFullscreenOnMobile. */
  fullscreenAlways?: boolean,

  /** The default Modal behavior is to display in fullscreen on mobile.  Set this flag to disable this functionality. */
  disableFullscreenOnMobile?: boolean,

  /** Handles CSS overflow of content in the Modal.
   *  Defaults to true where bodyContent handles y-scrolling for the entire Modal.
   *  When false, allows overflow of content to be handled on sub-elements when needed.
   */
  bodyScroll?: boolean,

  /**
   * Allow the content of the modal to render outside of it
   */
  overflowVisible?: boolean,

  /** Optional handler for modal dismiss */
  onClose?: () => mixed,

  /** Optional handler for modal scroll */
  onScroll?: (SyntheticEvent<mixed>) => mixed,

  /** drag/drop event handler that affects the entire modal including the padding */
  onDragOver?: (e: SyntheticDragEvent<*>) => void,

  /** drag/drop event handler that affects the entire modal including the padding */
  onDragLeave?: (e: SyntheticDragEvent<*>) => void,

  /** drag/drop event handler that affects the entire modal including the padding */
  onDrop?: (e: SyntheticDragEvent<*>) => void,

  /** Use height instead of max-height */
  fixedHeight?: boolean,

  /** Optionally include close X button. Defaults to true. */
  closeX?: boolean,

  /** Optional color scheme override for the close X button */
  closeXColorVariant?: CloseIconColorVariant,

  /** Inner contents of the Modal. */
  children: React.Node,

  /** Special case where we explicitly set offset from top of viewport to accommodate the situation
   * where we have an very high iframe of the TA site. */
  overlayTop?: ?number,

  /** Align the Modal to the bottom of the screen? */
  alignToBottom?: boolean,

  /** Whether to include body margin */
  noBodyPadding?: boolean,

  /** Whether or not the corners are rounded */
  roundedCorners?: boolean,

  /** Disables the 90vh height constraint */
  unconstrainedHeight?: boolean,

  /** The background color of the container, white by default */
  backgroundColor?: string,
|};

/**
 * @deprecated  Use @ta/modal.styleguide instead
 */
const ModalView = ({
  isFullBleed,
  header,
  fullscreenAlways,
  disableFullscreenOnMobile,
  noBodyPadding,
  bodyScroll,
  overflowVisible,
  onClose,
  onScroll,
  onDragOver,
  onDragLeave,
  onDrop,
  fixedHeight,
  closeX,
  closeXColorVariant,
  children,
  overlayTop,
  alignToBottom,
  roundedCorners,
  unconstrainedHeight,
  backgroundColor,
}: OuterProps) => {
  const height = fixedHeight ? 'calc(100% - 4rem)' : undefined;
  let vertical = 'centered';
  if (alignToBottom) {
    vertical = {
      bottom: 0,
      top: overlayTop ? `${overlayTop}px` : 'auto',
    };
  } else if (overlayTop) {
    vertical = {
      top: overlayTop,
    };
  }
  const containerProps = {
    overflowVisible: bodyScroll,
    backgroundColor,
    unconstrainedHeight,
    onScroll,
    roundedCorners,
  };

  let bodyScrollEnum;
  if (bodyScroll === false) {
    bodyScrollEnum = 'hidden';
  } else if (overflowVisible) {
    bodyScrollEnum = 'visible';
  }

  const bodyProps = {
    bodyScroll: bodyScrollEnum,
    isFullBleed: isFullBleed || noBodyPadding,
    onDragOver,
    onDragLeave,
    onDrop,
  };

  let fullscreen;
  if (fullscreenAlways) {
    fullscreen = 'always';
  } else if (disableFullscreenOnMobile) {
    fullscreen = 'never';
  }

  return (
    <Modal
      vertical={vertical}
      height={height}
      closeIconColor={closeXColorVariant}
      header={header}
      onClose={onClose}
      enableCloseIcon={closeX}
      containerProps={containerProps}
      bodyProps={bodyProps}
      fullscreen={fullscreen}
      disableAutoFocus
    >
      {children}
    </Modal>
  );
};

export default ModalView;
